import { usePaymentContext } from '#contexts/PaymentContext';
import { EPaymentType } from '#models/product';
import { useGetClientSecretId } from '#queries/payment';
import { useProductStyle } from '#utils/style';
import { Box, Button, CircularProgress, Grid, Input, makeStyles, Typography } from '@material-ui/core';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardNumberElement, StripeCardNumberElementOptions } from '@stripe/stripe-js';
import { colors } from '@swyft/ui';
import { useFormikContext } from 'formik';
import React from 'react';

const CARD_ELEMENT_OPTIONS: StripeCardNumberElementOptions = {
  showIcon: true,
  iconStyle: 'solid',
  style: {
    base: {
      color: colors.grey.DEFAULT,
      '::placeholder': {
        color: colors.grey[300]
      }
    },
    invalid: {
      color: colors.error.main,
      iconColor: colors.error.main
    }
  }
};

type CardError = {
  type: 'validation_error';
  code: string;
  message: string;
};

export const PaymentForm = () => {
  const classes = useStyles();
  const themeClasses = useProductStyle();
  const stripe = useStripe();
  const elements = useElements();
  const [nameOnCard, setNameOnCard] = React.useState<string>('');
  const [cardNumberError, setCardNumberError] = React.useState<CardError | undefined>(undefined);
  const [expireDateError, setExpireDateError] = React.useState<CardError | undefined>(undefined);
  const [CVVError, setCVVError] = React.useState<CardError | undefined>(undefined);
  const [isSubmitPayment, setIsSubmitPayment] = React.useState<boolean>(false);
  const {
    state: { paymentProduct }
  } = usePaymentContext();

  const { handleSubmit, isSubmitting } = useFormikContext();

  const onePrice = paymentProduct?.prices?.find((item) => item.paymentType === EPaymentType.ONE);

  const { mutateAsync: getClientSecretId } = useGetClientSecretId();

  const handleBooking = async () => {
    if (!stripe || !elements || cardNumberError || expireDateError || CVVError) {
      return;
    }
    try {
      setIsSubmitPayment(true);
      const { clientSecret } = await getClientSecretId({
        productId: paymentProduct?.id || '',
        priceId: onePrice?.id || ''
      });

      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement) as StripeCardNumberElement,
          billing_details: { name: nameOnCard }
        }
      });

      if (payload.error) {
        return;
      }

      handleSubmit();
    } finally {
      setIsSubmitPayment(false);
    }
  };

  if (!stripe || !elements) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' className='w-full h-full'>
        <CircularProgress color='primary' />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        px={{ xs: 3.5, xl: 5 }}
        py={3}
        borderRadius={3}
        border={`1px solid ${colors.grey[50]}`}
        display='flex'
        flexDirection='column'
        width='100%'
        mt={3}
        maxWidth={700}
        mx='auto'
      >
        <Box mb={2}>
          <Typography className='semiBold'>Credit Card</Typography>
        </Box>
        <Grid spacing={2} container className={classes.customPaymentForm}>
          <Grid item xs={12}>
            <Typography variant='caption' className='labelText'>
              CARD NUMBER
            </Typography>
            <CardNumberElement options={CARD_ELEMENT_OPTIONS} onChange={(e) => setCardNumberError(e.error)} />
            <Typography variant='caption' color='error'>
              {cardNumberError && cardNumberError.message}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant='caption' className='labelText'>
              NAME ON CARD
            </Typography>
            <Input value={nameOnCard} onChange={(e) => setNameOnCard(e.target.value)} fullWidth disableUnderline />
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant='caption' className='labelText'>
              EXPIRE DATE
            </Typography>
            <CardExpiryElement options={CARD_ELEMENT_OPTIONS} onChange={(e) => setExpireDateError(e.error)} />
            <Typography variant='caption' color='error'>
              {expireDateError && expireDateError.message}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant='caption' className='labelText'>
              CVV CODE
            </Typography>
            <CardCvcElement options={CARD_ELEMENT_OPTIONS} onChange={(e) => setCVVError(e.error)} />
            <Typography variant='caption' color='error'>
              {CVVError && CVVError.message}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mx='auto' my={3} width={{ xs: '100%', sm: '50%' }} height={40}>
        <Button
          onClick={handleBooking}
          variant='contained'
          className={`${themeClasses.button} h-full`}
          fullWidth
          disabled={isSubmitPayment || isSubmitting}
        >
          Confirm Booking
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  customPaymentForm: {
    '& input, .StripeElement': {
      display: 'block',
      border: `1px solid ${colors.grey[50]}`,
      padding: '1rem 1.5rem',
      borderRadius: 3,
      fontSize: '0.875rem',
      '&.StripeElement--invalid': {
        borderColor: colors.error.main
      }
    }
  }
});
